import React, { useMemo } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
} from "@mui/material";
import {
  TimePeriod,
  TimePeriodSelection as TimePeriodSelectionType,
} from "@store/slices/crosstab-config-slice";
import DateInput from "../common/DateInput";
import {
  useGetDatasetByIdQuery,
  useGetDatasetTimeBoundsQuery,
} from "@store/api/dataset-api";
import { useAppSelector } from "@store/index";
import {
  getLatestQuarterDates,
  getPreviousQuarterDates,
  getLatestSemesterDates,
  getPreviousSemesterDates,
} from "@/utils/time-period-utils";
import { buildDefaultDateGuardrails } from "@models/Dataset";

interface TimePeriodSelectionProps {
  timePeriodSelection: TimePeriodSelectionType;
  onTimePeriodChange: (timePeriod: TimePeriod) => void;
  onTimePeriodDatesChange: (dates: {
    startDate: string | null;
    endDate: string | null;
  }) => void;
  disabled?: boolean;
  statisticalTestingEnabled?: boolean;
  responsesMinDate?: string;
  responsesMaxDate?: string;
}

const TimePeriodSelection: React.FC<TimePeriodSelectionProps> = ({
  timePeriodSelection,
  onTimePeriodChange,
  onTimePeriodDatesChange,
  disabled = false,
  statisticalTestingEnabled = false,
  responsesMinDate,
  responsesMaxDate,
}) => {
  const datasetId = useAppSelector(
    (state) =>
      state.chartConfig.datasetId ||
      state.crosstabConfig.currentConfig.datasetId ||
      state.tableConfig.datasetId,
  );
  const { data: dataset } = useGetDatasetByIdQuery(datasetId as string, {
    skip: !datasetId,
  });
  const { data: timeBounds } = useGetDatasetTimeBoundsQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  // Derive guardrail unit availability from dataset (fall back to all-allowed defaults)
  const pitGuardrails = useMemo(() => {
    return dataset?.dateGuardrails?.pointInTime ?? buildDefaultDateGuardrails().pointInTime;
  }, [dataset?.dateGuardrails]);

  const quarterEnabled = pitGuardrails.units.quarter.enabled;
  const semesterEnabled = pitGuardrails.units.semester.enabled;

  const availabilityNote = useMemo(() => {
    const cutoffStr = dataset?.dataAvailabilityDate || timeBounds?.maxDate;
    if (!cutoffStr) return null;
    // Force local-time parse: ISO date-only strings are UTC by default, which shifts
    // the date one day back in negative-offset timezones and produces wrong quarters.
    const localStr = /^\d{4}-\d{2}-\d{2}$/.test(cutoffStr)
      ? cutoffStr + "T00:00:00"
      : cutoffStr;
    const cutoff = new Date(localStr);
    if (isNaN(cutoff.getTime())) return null;

    const displayDate = cutoff.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const base = `Time periods resolve based on dataset availability through ${displayDate}.`;
    const tp = timePeriodSelection?.timePeriod;

    const quarterLabel = (d: Date) => `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
    const semesterLabel = (d: Date) => `S${d.getMonth() < 6 ? 1 : 2} ${d.getFullYear()}`;

    // startDates from the utility are YYYY-MM-DD; parse as local time for correct month.
    const parseLocal = (s: string) => new Date(s + "T00:00:00");
    const periodSuffix: Partial<Record<string, string>> = {
      latest_quarter: `Latest Quarter is ${quarterLabel(parseLocal(getLatestQuarterDates(cutoff).startDate))}.`,
      previous_quarter: `Previous Quarter is ${quarterLabel(parseLocal(getPreviousQuarterDates(cutoff).startDate))}.`,
      latest_semester: `Latest Semester is ${semesterLabel(parseLocal(getLatestSemesterDates(cutoff).startDate))}.`,
      previous_semester: `Previous Semester is ${semesterLabel(parseLocal(getPreviousSemesterDates(cutoff).startDate))}.`,
    };

    if (tp && periodSuffix[tp]) return `${base} ${periodSuffix[tp]}`;

    return base;
  }, [dataset?.dataAvailabilityDate, timePeriodSelection?.timePeriod]);

  // NOTE: useEffect removed to prevent automatic re-fetching
  // All date changes now happen explicitly through user actions in handleTimePeriodChange

  // Validate date range
  const dateError = useMemo(() => {
    if (
      timePeriodSelection?.timePeriod === "custom_period" &&
      timePeriodSelection?.startDate &&
      timePeriodSelection?.endDate
    ) {
      const startDate = new Date(timePeriodSelection.startDate);
      const endDate = new Date(timePeriodSelection.endDate);

      if (endDate < startDate) {
        return "End date cannot be earlier than start date";
      }
    }
    return null;
  }, [
    timePeriodSelection?.timePeriod,
    timePeriodSelection?.startDate,
    timePeriodSelection?.endDate,
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControl fullWidth size="small">
        <FormLabel className="text-[12px] text-[#262626]">
          Time Period
        </FormLabel>
        <Select
          value={timePeriodSelection?.timePeriod || "all_time"}
          onChange={(e) => onTimePeriodChange(e.target.value as TimePeriod)}
          disabled={disabled}
        >
          <MenuItem value="all_time">All Time</MenuItem>
          {quarterEnabled && (
            <MenuItem value="latest_quarter">Latest Quarter</MenuItem>
          )}
          {quarterEnabled && (
            <MenuItem value="previous_quarter">Previous Quarter</MenuItem>
          )}
          {semesterEnabled && (
            <MenuItem value="latest_semester">Latest Semester</MenuItem>
          )}
          {semesterEnabled && (
            <MenuItem value="previous_semester">Previous Semester</MenuItem>
          )}
          <MenuItem value="custom_period">Custom Period</MenuItem>
        </Select>
      </FormControl>

      {timePeriodSelection?.timePeriod === "custom_period" && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <DateInput
              label="Start Date"
              value={timePeriodSelection?.startDate || ""}
              onChange={(e) => {
                onTimePeriodDatesChange({
                  startDate: e,
                  endDate: timePeriodSelection?.endDate || null,
                });
              }}
              disabled={disabled}
            />
            <DateInput
              label="End Date"
              value={timePeriodSelection?.endDate || ""}
              onChange={(e) => {
                onTimePeriodDatesChange({
                  startDate: timePeriodSelection?.startDate || null,
                  endDate: e,
                });
              }}
              disabled={disabled}
            />
          </Box>
          {dateError && (
            <Alert severity="error" sx={{ fontSize: "0.75rem" }}>
              {dateError}
            </Alert>
          )}
        </>
      )}

      {availabilityNote && (
        <Typography variant="caption" color="text.secondary">
          {availabilityNote}
        </Typography>
      )}
    </Box>
  );
};

export default TimePeriodSelection;
